import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Pencil,
  Camera,
  ArrowLeft,
  UserRound,
  ShieldCheck,
  CalendarDays,
  LogOut,
  Lock,
  FileText,
  Check,
  AlertCircle,
} from "lucide-react";
import axiosInstance from "../api/Axiosinstance";
import { ENDPOINTS } from "../api/Endpoints";
import providersService from "../api/services/providersService";
import { useToast } from "../hooks/useToast";
import {
  uploadFile,
  validateUploadFile,
  UPLOAD_PURPOSES,
} from "../utils/directUpload";
import {
  POLICY_EFFECTIVE_DATE,
  PRIVACY_POLICY_ITEMS,
  TERMS_CONDITION_ITEMS,
} from "../data/settingsPolicyContent";
import { useAuthStore } from "../hooks/useAuthStore";
import { authStore } from "../stores/auth.store";
import ProfileSkeleton from "../components/ProfileSkeleton";
import HeaderDash from "../components/HeaderDash";
import HeaderAssistant from "../components/HeaderAssistant";
import useAppConfig from "../hooks/useAppConfig";

// Simple profile management page; currently reads/writes to stored user data.
export default function ProfileManagement() {
  const { data: config } = useAppConfig();
  const privacyItems = config?.privacyPolicy?.items?.length > 0 ? config.privacyPolicy.items : PRIVACY_POLICY_ITEMS;
  const privacyDate = config?.privacyPolicy?.lastUpdated ? new Date(config.privacyPolicy.lastUpdated).toLocaleDateString() : POLICY_EFFECTIVE_DATE;
  const termsItems = config?.termsAndConditions?.items?.length > 0 ? config.termsAndConditions.items : TERMS_CONDITION_ITEMS;
  const termsDate = config?.termsAndConditions?.lastUpdated ? new Date(config.termsAndConditions.lastUpdated).toLocaleDateString() : POLICY_EFFECTIVE_DATE;

  const navigate = useNavigate();

  const location = useLocation();
  const { user: storedUser, isAuthenticated, actions } = useAuthStore();
  // Bookings live on My Trips; assistants manage theirs from the dashboard.
  const isTraveller = storedUser?.role === "user";
  const toast = useToast();

  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    phone: "",
    avatar: "",
  });
  const avatarInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("personal");
  // The picked file itself, uploaded on save. Holding the File rather than a
  // base64 string is what keeps a phone photo out of the JSON request body.
  const [avatarFile, setAvatarFile] = useState(null);
  // Snapshot of the saved values, so "Save changes" can tell an edited form
  // from an untouched one.
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSection, setMobileSection] = useState("menu");
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (!tabParam) return;
    const nextTab = tabParam.toLowerCase();
    // Bookings moved from the profile's History tab to My Trips. Links already
    // out there — old notifications, bookmarks — still point at the old tab.
    if (nextTab === "history" && isTraveller) {
      const bookingId = params.get("booking");
      navigate(
        bookingId ? `/user/trips/${encodeURIComponent(bookingId)}` : "/user/trips",
        { replace: true }
      );
      return;
    }
    const allowedTabs = new Set(["personal", "password", "privacy", "terms"]);
    if (!allowedTabs.has(nextTab)) return;
    setActiveTab(nextTab);
    setMobileSection(nextTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const extractPhone = useCallback((data = {}) =>
    data.phone ||
    data.phoneNumber ||
    data.mobile ||
    data.mobileNumber ||
    data.phone_number ||
    data.mobile_number ||
    data.phoneNo ||
    data.mobile_no ||
    data.mobilePhone ||
    data.mobile_phone ||
    data.mobile_phone_number ||
    data.phone_number_formatted ||
    data.msisdn ||
    data.contactNumber ||
    data.contact, []);

  const getAvatarFromData = useCallback((data = {}) =>
    data.avatar ||
    (typeof data.profilePicture === "string" ? data.profilePicture : data.profilePicture?.url) ||
    "", []);

  const handleLogout = useCallback(async () => {
    await actions.logout();
    navigate("/auth/login", { replace: true });
  }, [navigate, actions]);

  const updateStoredUserAvatar = useCallback((avatar, profilePictureSource) => {
    const currentUser = storedUser || {};
    const updatedUser = {
      ...currentUser,
      avatar,
      profilePicture: profilePictureSource || avatar,
    };
    actions.setUser(updatedUser);
  }, [storedUser, actions]);

  const mergeProfileFromApi = useCallback((data = {}) => {
    const fullName =
      data.fullName || [data.firstName, data.lastName].filter(Boolean).join(" ").trim();
    const phoneFromApi = extractPhone(data);
    const avatarFromApi = getAvatarFromData(data);
    const avatar = avatarFromApi || "";
    const resolvedAvatar = avatar || "";

    setProfile((prev) => ({
      ...prev,
      fullName: fullName || prev.fullName,
      email: data.email || prev.email,
      phone: phoneFromApi || prev.phone,
      avatar: avatar || prev.avatar || "",
    }));

    const updatedUser = {
      ...data,
      fullName: fullName || data.fullName,
      email: data.email,
      phone: phoneFromApi,
      mobile: phoneFromApi,
      avatar: resolvedAvatar,
      profilePicture: data.profilePicture || avatar,
    };

    actions.setUser(updatedUser);
  }, [extractPhone, getAvatarFromData, actions]);

  // Guard so we only fetch the account once on mount, regardless of storedUser identity changes.
  // Without this ref, mergeProfileFromApi → actions.setUser → new storedUser reference → effect
  // re-runs → infinite API loop.
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !storedUser) {
      navigate("/auth/login");
      return;
    }

    // Seed local form state from whatever is already in the store (instant, no flicker)
    if (storedUser) {
      try {
        const fullName =
          storedUser.fullName ||
          [storedUser.firstName, storedUser.lastName].filter(Boolean).join(" ").trim();
        const phoneFromStorage = extractPhone(storedUser);
        const avatarFromStorage =
          storedUser.avatar ||
          (typeof storedUser.profilePicture === "string"
            ? storedUser.profilePicture
            : storedUser.profilePicture?.url) ||
          "";

        setProfile((prev) => ({
          ...prev,
          fullName: fullName || prev.fullName,
          email: storedUser.email || prev.email,
          phone: phoneFromStorage || prev.phone,
          avatar: avatarFromStorage,
        }));
      } catch {
        navigate("/auth/login");
        return;
      }
    }

    // Only hit the API once — subsequent storedUser identity changes (from setUser inside
    // mergeProfileFromApi) must NOT re-trigger this effect.
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchAccount = async () => {
      setLoadingProfile(true);
      try {
        const response = await axiosInstance.get(ENDPOINTS.AUTH.ACCOUNT);
        const account =
          response.data?.data?.user || response.data?.data || response.data || {};
        mergeProfileFromApi(account);

        const accountAvatar = getAvatarFromData(account);
        if (!accountAvatar) {
          try {
            const profileResponse = await providersService.getProfile();
            const providerProfile =
              profileResponse?.data?.profile ||
              profileResponse?.data ||
              profileResponse?.profile ||
              profileResponse;
            const providerAvatar = getAvatarFromData(providerProfile);
            if (providerAvatar) {
              setProfile((prev) => ({ ...prev, avatar: providerAvatar }));
              // Read storedUser from the store directly to avoid stale closure
              const currentUser = authStore.user || {};
              authStore.setUser({
                ...currentUser,
                avatar: providerAvatar,
                profilePicture: providerProfile?.profilePicture || providerAvatar,
              });
            }
          } catch {
            // Provider profile fetch is best-effort; primary account data is sufficient.
          }
        }
      } catch (err) {
        const statusCode = err.response?.status;
        if (statusCode === 401 || statusCode === 403) {
          handleLogout();
        } else {
          toast.apiError(err, "Unable to fetch profile.");
          setLoadingProfile(false);
        }
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // intentionally narrow: only re-run if auth state changes (login/logout)

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.matchMedia("(max-width: 768px)").matches);
      }
    };

    if (typeof window === "undefined") return undefined;

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Capture the saved values once the profile has settled. Avatar is excluded
  // on purpose — a photo only counts as a change when the user picks a file,
  // never because a later request filled the field in.
  useEffect(() => {
    if (loadingProfile || baseline) return;
    setBaseline({
      fullName: profile.fullName || "",
      email: profile.email || "",
      phone: profile.phone || "",
    });
  }, [loadingProfile, baseline, profile.fullName, profile.email, profile.phone]);

  // Release the last preview URL when the page goes away.
  useEffect(
    () => () => {
      setProfile((prev) => {
        if (prev.avatar?.startsWith("blob:")) URL.revokeObjectURL(prev.avatar);
        return prev;
      });
    },
    []
  );

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);

    const isComplete = Boolean(profile.fullName && profile.email && profile.phone);
    const parsed = storedUser || {};

    const sanitizedPhone = profile.phone?.toString().replace(/[^\d+]/g, "") || "";

    const payload = {
      fullName: profile.fullName?.trim() || undefined,
      email: profile.email?.trim() || undefined,
      mobile: sanitizedPhone || undefined,
    };

    try {
      // The photo goes browser → Cloudinary; the API only ever sees the id it
      // resolves to. Nothing image-sized is ever put in the request body, which
      // is what used to blow past the JSON limit and surface as a size error.
      if (avatarFile) {
        const { publicId } = await uploadFile(
          avatarFile,
          UPLOAD_PURPOSES.PROFILE_PICTURE
        );
        payload.publicId = publicId;
      }

      const response = await axiosInstance.put(ENDPOINTS.AUTH.UPDATE_ACCOUNT, payload);
      // Cloudinary's URL for the freshly uploaded photo, so the preview stops
      // depending on a blob URL that dies with the page.
      const savedAvatar =
        getAvatarFromData(response?.data?.data?.user || response?.data?.data) ||
        profile.avatar;

      // normalize what we keep locally so it survives reloads/logins
      const normalizedPhone = sanitizedPhone || profile.phone;
      const updatedUser = {
        ...parsed,
        fullName: profile.fullName,
        email: profile.email,
        phone: normalizedPhone,
        mobile: normalizedPhone,
        avatar: savedAvatar,
        profilePicture: savedAvatar,
        profileCompleted: isComplete,
      };

      actions.setUser(updatedUser);
      setProfile((prev) => {
        if (prev.avatar?.startsWith("blob:") && savedAvatar !== prev.avatar) {
          URL.revokeObjectURL(prev.avatar);
        }
        return { ...prev, phone: normalizedPhone, avatar: savedAvatar };
      });

      toast.success("Profile saved.");
      setAvatarFile(null);
      // These values are the saved state now, so the form is clean again.
      setBaseline({
        fullName: profile.fullName || "",
        email: profile.email || "",
        phone: normalizedPhone || "",
      });

      if (isComplete) {
        navigate("/user/travel-assistant", { replace: true });
      }
    } catch (err) {
      toast.apiError(err, "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordInput = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordSave = async () => {
    if (!passwordFieldsComplete) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Password does not match.");
      return;
    }

    setPasswordSaving(true);
    try {
      await axiosInstance.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.apiError(err, "Unable to update password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0];
    // Let the same file be re-picked after an error.
    event.target.value = "";
    if (!file) return;

    const validationError = validateUploadFile(file, UPLOAD_PURPOSES.PROFILE_PICTURE);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Preview from an object URL rather than a base64 copy of the whole file.
    setProfile((prev) => {
      if (prev.avatar?.startsWith("blob:")) URL.revokeObjectURL(prev.avatar);
      return { ...prev, avatar: URL.createObjectURL(file) };
    });
    setAvatarFile(file);
  };

  const formatName = (value) => {
    if (!value) return "";
    return value.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const displayFullName = formatName(profile.fullName);
  const passwordMismatch =
    Boolean(passwordForm.newPassword && passwordForm.confirmPassword) &&
    passwordForm.newPassword !== passwordForm.confirmPassword;

  const isFormComplete = Boolean(
    profile.fullName?.trim() && profile.email?.trim() && profile.phone?.trim()
  );

  const passwordFieldsComplete = Boolean(
    passwordForm.currentPassword && passwordForm.newPassword && passwordForm.confirmPassword
  );

  // A new photo is always a change; the text fields are compared against the
  // values last loaded or saved, so an untouched form leaves the button inert.
  const hasUnsavedChanges = Boolean(
    avatarFile ||
      (baseline &&
        ((profile.fullName || "").trim() !== (baseline.fullName || "").trim() ||
          (profile.email || "").trim() !== (baseline.email || "").trim() ||
          (profile.phone || "").trim() !== (baseline.phone || "").trim()))
  );

  const personalSaveDisabled =
    loadingProfile || !isFormComplete || !hasUnsavedChanges || saving;
  const personalSaveLabel = loadingProfile ? "Loading..." : saving ? "Saving..." : "Save changes";

  // One source of truth for both layouts. The mobile button used to paint the
  // same washed-out pink whether or not it was clickable, so a ready form read
  // as a dead one; the difference is now the colour, not a hover state.
  const personalSaveClasses = (base) =>
    `${base} ${
      personalSaveDisabled
        ? "bg-[#c8a3b6]/70 cursor-not-allowed"
        : "bg-[#61013B] hover:bg-[#4f0f32]"
    }`;

  const navItems = [
    { key: "personal", label: "Personal Information", icon: UserRound },
    { key: "password", label: "Change Password", icon: Lock },
    { key: "privacy", label: "Privacy Policy", icon: ShieldCheck },
    { key: "terms", label: "Terms & Condition", icon: FileText },
    ...(isTraveller
      ? [{ key: "trips", label: "My Trips", icon: CalendarDays, to: "/user/trips" }]
      : []),
  ];

  if (loadingProfile) {
    return <ProfileSkeleton isMobile={isMobile} />;
  }

  if (isMobile) {
    return (
      <div className="min-h-screen bg-white px-4 pb-12 pt-10">
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarUpload}
        />

        {mobileSection === "menu" && (
          <>
            <div className="mx-auto flex max-w-lg flex-col items-center text-center">
              <div className="mb-4 h-40 w-40 overflow-hidden rounded-full bg-gray-200 shadow">
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.fullName || "User avatar"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-gray-500">
                    {(profile.fullName || "U").charAt(0)}
                  </div>
                )}
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">{profile.fullName || "Traveler"}</h1>
              <p className="mt- text-lg font-medium text-gray-500">{profile.email || ""}</p>

              <div className="mt-8 flex w-full flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("personal");
                    setMobileSection("personal");
                  }}
                  className="flex items-center gap-3 rounded-2xl border border-gray-300 px-4 py-4 text-left text-lg font-medium text-gray-800 shadow-sm"
                >
                  <UserRound className="h-6 w-6 text-gray-700" />
                  Personal Information
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("password");
                    setMobileSection("password");
                  }}
                  className="flex items-center gap-3 rounded-2xl border border-gray-300 px-4 py-4 text-left text-lg font-medium text-gray-800 shadow-sm"
                >
                  <Lock className="h-6 w-6 text-gray-700" />
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("privacy");
                    setMobileSection("privacy");
                  }}
                  className="flex items-center gap-3 rounded-2xl border border-gray-300 px-4 py-4 text-left text-lg font-medium text-gray-800 shadow-sm"
                >
                  <ShieldCheck className="h-6 w-6 text-gray-700" />
                  Privacy Policy
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("terms");
                    setMobileSection("terms");
                  }}
                  className="flex items-center gap-3 rounded-2xl border border-gray-300 px-4 py-4 text-left text-lg font-medium text-gray-800 shadow-sm"
                >
                  <FileText className="h-6 w-6 text-gray-700" />
                  Terms &amp; Condition
                </button>
                {isTraveller && (
                  <button
                    type="button"
                    onClick={() => navigate("/user/trips")}
                    className="flex items-center gap-3 rounded-2xl border border-gray-300 px-4 py-4 text-left text-lg font-medium text-gray-800 shadow-sm"
                  >
                    <CalendarDays className="h-6 w-6 text-gray-700" />
                    My Trips
                  </button>
                )}
              </div>
                 
              <button
                type="button"
                onClick={handleLogout}
                className="mt-20 inline-flex items-center gap-2 text-xl font-semibold text-red-600"
              >
                <LogOut className="h-6 w-6" />
                Log Out
              </button>
            </div>
          </>
        )}

        {mobileSection === "personal" && (
          <div className="mx-auto flex max-w-lg flex-col gap-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileSection("menu")}
                className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
            </div>

            <div className="flex flex-col items-center gap-3 text-center">
              <div className="relative h-36 w-36 overflow-hidden rounded-full bg-gray-200 shadow">
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.fullName || "User avatar"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-gray-500">
                    {(profile.fullName || "U").charAt(0)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md transition hover:shadow-lg"
                >
                  <Camera className="h-5 w-5 text-gray-700" />
                </button>
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900">{displayFullName || "Traveler"}</p>
                <p className="text-base font-medium text-gray-500">{profile.email || ""}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Full name</label>
                <div className="flex items-center gap-3 rounded-2xl border border-[#d6dce3] px-4 py-3">
                  <input
                    type="text"
                    value={profile.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    placeholder="Your full name"
                    className="w-full bg-transparent text-base font-semibold text-gray-800 outline-none placeholder:text-gray-400"
                  />
                  <Pencil className="h-5 w-5 text-gray-500" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <div className="flex items-center gap-3 rounded-2xl border border-[#d6dce3] px-4 py-3">
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="Email address"
                    className="w-full bg-transparent text-base font-semibold text-gray-800 outline-none placeholder:text-gray-400"
                  />
                  <Pencil className="h-5 w-5 text-gray-500" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Phone no</label>
                <div className="flex items-center gap-3 rounded-2xl border border-[#d6dce3] px-4 py-3">
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="+234xxxxxxxxxx"
                    className="w-full bg-transparent text-base font-semibold text-gray-800 outline-none placeholder:text-gray-400"
                  />
                  <Pencil className="h-5 w-5 text-gray-500" />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={personalSaveDisabled}
              className={personalSaveClasses(
                "mt-6 w-full rounded-2xl px-4 py-4 text-lg font-semibold text-white transition"
              )}
            >
              {personalSaveLabel}
            </button>
          </div>
        )}

        {mobileSection === "password" && (
          <div className="mx-auto flex max-w-lg flex-col gap-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileSection("menu")}
                className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
            </div>

            <div className="space-y-1">
              <h3 className="text-2xl font-bold text-[#6b0c44]">Set New Password</h3>
              <p className="text-base font-medium text-gray-600">
                Create a new password you can remember easily
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Current Password</label>
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#e7d6dd] px-4 py-4">
                  <input
                    type={showPassword.currentPassword ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(e) => handlePasswordInput("currentPassword", e.target.value)}
                    placeholder="Current password"
                    className="w-full bg-transparent text-lg font-semibold text-gray-700 outline-none placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((prev) => ({
                        ...prev,
                        currentPassword: !prev.currentPassword,
                      }))
                    }
                    className="text-sm font-semibold text-[#6b0c44]"
                  >
                    {showPassword.currentPassword ? "hide" : "show"}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">New Password</label>
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#e7d6dd] px-4 py-4">
                  <input
                    type={showPassword.newPassword ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => handlePasswordInput("newPassword", e.target.value)}
                    placeholder="New password"
                    className="w-full bg-transparent text-lg font-semibold text-gray-700 outline-none placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((prev) => ({
                        ...prev,
                        newPassword: !prev.newPassword,
                      }))
                    }
                    className="text-sm font-semibold text-[#6b0c44]"
                  >
                    {showPassword.newPassword ? "hide" : "show"}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Repeat Password</label>
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#e7d6dd] px-4 py-4">
                  <input
                    type={showPassword.confirmPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => handlePasswordInput("confirmPassword", e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full bg-transparent text-lg font-semibold text-gray-700 outline-none placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((prev) => ({
                        ...prev,
                        confirmPassword: !prev.confirmPassword,
                      }))
                    }
                    className="text-sm font-semibold text-[#6b0c44]"
                  >
                    {showPassword.confirmPassword ? "hide" : "show"}
                  </button>
                </div>
              </div>

              {passwordMismatch && (
                <p className="flex items-center gap-2 text-sm font-medium text-[#e11d48]">
                  <AlertCircle className="h-4 w-4" />
                  Password does not match
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                if (passwordMismatch) {
                  toast.error("Password does not match.");
                  return;
                }
                handlePasswordSave();
              }}
              disabled={!passwordFieldsComplete || passwordSaving}
              className={`mt-6 w-full rounded-2xl px-4 py-4 text-lg font-semibold text-white transition ${
                !passwordFieldsComplete || passwordSaving
                  ? "bg-[#caa8b8] opacity-80"
                  : "bg-[#caa8b8] hover:opacity-90"
              }`}
            >
              {passwordSaving ? "Saving..." : "Save changes"}
            </button>
            {passwordSuccess && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 py-8">
                <div className="w-full max-w-md rounded-3xl bg-white px-6 py-10 text-center shadow-2xl">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-50 text-green-700">
                    <Check className="h-10 w-10" strokeWidth={2.5} />
                  </div>
                  <div className="mt-6 space-y-2">
                    <h3 className="text-2xl font-semibold text-[#6b0c44]">Password Reset</h3>
                    <p className="text-base font-medium text-gray-600">
                      You have successfully changed your password
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPasswordSuccess(false);
                      setMobileSection("menu");
                    }}
                    className="mt-8 w-full rounded-2xl border border-[#6b0c44] px-4 py-3 text-lg font-semibold text-[#6b0c44] transition hover:bg-[#f6edf3]"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {mobileSection === "privacy" && (
          <div className="mx-auto flex max-w-lg flex-col gap-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileSection("menu")}
                className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Privacy Policy</h2>
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-[#6b0c44]">Privacy Policy</h3>
              <p className="text-sm font-semibold text-gray-700">Effective Date: To be updated</p>
              <ol className="space-y-3 text-base font-medium text-gray-700 list-decimal list-inside">
                <li>
                  <span className="font-semibold">Information We Collect</span> Personal info, contact details, location
                  data, vehicle details, property details, transaction and payout data.
                </li>
                <li>
                  <span className="font-semibold">How We Use Information</span> To process bookings, payments, refunds,
                  payouts, validation alerts, customer support, and service improvements.
                </li>
                <li>
                  <span className="font-semibold">Wallet &amp; Refund Data</span> Wallet and refund data is stored for
                  dispute resolution and financial reconciliation.
                </li>
                <li>
                  <span className="font-semibold">Service Validation Data</span> We send essential notifications for
                  service start, status, and completion. These cannot be opted out of during active bookings.
                </li>
                <li>
                  <span className="font-semibold">Assistant Payout Data</span> We process payout data including payment
                  splits, commission deductions, and satisfaction confirmations for settlement.
                </li>
                <li>
                  <span className="font-semibold">Data Sharing</span> Data may be shared with Assistants, Hosts, or
                  payment providers for service delivery. Pulo does not sell personal data.
                </li>
                <li>
                  <span className="font-semibold">Data Security</span> We take reasonable steps to protect information
                  but cannot guarantee absolute security.
                </li>
                <li>
                  <span className="font-semibold">User Rights</span> Users may request access, correction, or deletion of
                  data.
                </li>
                <li>
                  <span className="font-semibold">Data Retention</span> Data is retained as long as needed for service
                  delivery or legal compliance.
                </li>
                <li>
                  <span className="font-semibold">Policy Updates</span> Policy may be updated. Continued use constitutes
                  acceptance.
                </li>
              </ol>
            </div>
          </div>
        )}

        {mobileSection === "terms" && (
          <div className="mx-auto flex max-w-lg flex-col gap-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileSection("menu")}
                className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Terms &amp; Conditions</h2>
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-[#6b0c44]">Terms &amp; Conditions</h3>
              <p className="text-sm font-semibold text-gray-700">Effective Date: To be updated</p>
              <ol className="space-y-3 text-base font-medium text-gray-700 list-decimal list-inside">
                <li>
                  <span className="font-semibold">Eligibility</span> You must be at least 18 years old to use Pulo.
                </li>
                <li>
                  <span className="font-semibold">Platform Overview</span> Pulo is a digital platform that connects users
                  with independent travel assistants (Pulo Assistants) and accommodation providers (Hosts). Pulo does
                  not directly provide transportation or accommodation services.
                </li>
                <li>
                  <span className="font-semibold">User Accounts</span> Users must provide accurate and up-to-date
                  information and are responsible for their account security.
                </li>
                <li>
                  <span className="font-semibold">Bookings &amp; Payments</span> Users are required to pay 100% of the
                  service fee at the point of booking. Payment confirms scheduling and is held in escrow until service
                  completion.
                </li>
                <li>
                  <span className="font-semibold">Refund &amp; Dispute Resolution</span> Users may request refunds which
                  will be reviewed by Customer Support and may result in full, partial, or no refund.
                </li>
                <li>
                  <span className="font-semibold">Wallet System</span> Wallet credits may be issued from refunds or
                  promotions. Wallet credits are non-withdrawable, non-transferable, and cannot be funded by users.
                </li>
                <li>
                  <span className="font-semibold">Service Validation Alerts</span> Users receive alerts near service
                  start to validate that the assistant has begun work. Validation affects refund eligibility.
                </li>
                <li>
                  <span className="font-semibold">Assistant Payout Structure</span> Pulo Assistants receive 50% payout
                  once the service starts. The remaining 50% is released after service completion and customer
                  satisfaction confirmation.
                </li>
                <li>
                  <span className="font-semibold">Revenue Model</span> Pulo charges a 30% commission on every booking
                  before payouts to assistants.
                </li>
                <li>
                  <span className="font-semibold">Independent Providers</span> Assistants and Hosts are independent
                  contractors, not employees of Pulo.
                </li>
                <li>
                  <span className="font-semibold">Safety &amp; Liability</span> Pulo is not responsible for actions or
                  omissions of independent providers or users.
                </li>
                <li>
                  <span className="font-semibold">Account Suspension</span> Pulo may suspend or terminate accounts for
                  violations.
                </li>
                <li>
                  <span className="font-semibold">Modifications</span> Terms may be updated. Continued use constitutes
                  acceptance.
                </li>
                <li>
                  <span className="font-semibold">Governing Law</span> These Terms are governed by the laws of the
                  Federal Republic of Nigeria.
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {storedUser?.role === "serviceProvider" ? (
        <HeaderAssistant />
      ) : (
        <HeaderDash />
      )}
      <div className="min-h-screen bg-white text-[#2B333C] ">
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarUpload}
      />
      <div className="mx-auto flex flex-col gap-10 lg:flex-row border-t border-[#e6e8ef] h-[90vh]">
        <aside className="w-full border-b border-[#e6e8ef] px-6 pt-10 lg:w-72 lg:border-b-0 lg:border-r flex flex-col">
          <nav className="flex flex-col gap-2">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => (item.to ? navigate(item.to) : setActiveTab(item.key))}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left text-md font-medium transition ${
                  activeTab === item.key
                    ? "bg-[#64053f] text-white shadow-sm"
                    : "text-gray-700 hover:bg-[#f0f2f5]"
                }`}
                aria-pressed={activeTab === item.key}
              >
                <item.icon className={`h-5 w-5 ${activeTab === item.key ? "text-white" : "text-gray-700"}`} />
                <span className="text-">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="mt-auto mb-10 pt-8">
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-xl px-8 py-2 text-sm font-semibold text-[#d11a2a] transition hover:text-[#a91521]"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </aside>

        <section className="flex-1 px-2 py-10">
          {activeTab === "personal" && (
            <div className="mx-aut flex max-w-3xl flex-col items-stretch gap-10">
              <div className="flex flex-col gap-4">
                <h2 className="w-full text-left text-3xl font-bold text-[#6b0c44]">Personal Information</h2>
                <div className="relative h-40 w-40 overflow-hidden rounded-full border-4 border-white bg-[#f7f3f6] shadow-md">
                  {profile.avatar ? (
                    <img src={profile.avatar} alt="Profile avatar" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#7a0051]">
                      <Camera className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    className="absolute -bottom-3 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-white text-[#7a0051] shadow-md ring-1 ring-[#e6e8ef]"
                    aria-label="Upload profile photo"
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-xl font-semibold text-[#2d3340]">{displayFullName || "Your name"}</p>
                  <p className="text-sm text-[#6b7381]">{profile.email || "email@example.com"}</p>
                </div>
              </div>

              <div className="space-y-5">
                <label className="block text-sm font-semibold text-gray-700">
                  Full Name
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#d5dce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#7a8ba3] focus-within:ring-1 focus-within:ring-[#7a8ba3]/40">
                    <input
                      type="text"
                      value={profile.fullName}
                      onChange={(e) => handleChange("fullName", e.target.value)}
                      placeholder="Your full name"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#9aa3b3]"
                    />
                    <Pencil className="h-5 w-5 text-[#6b7381]" />
                  </div>
                </label>

                <label className="block text-sm font-semibold text-gray-700">
                  Email
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#d5dce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#7a8ba3] focus-within:ring-1 focus-within:ring-[#7a8ba3]/40">
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="name@email.com"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#9aa3b3]"
                    />
                    <Pencil className="h-5 w-5 text-[#6b7381]" />
                  </div>
                </label>

                <label className="block text-sm font-semibold text-gray-700">
                  Phone No
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#d5dce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#7a8ba3] focus-within:ring-1 focus-within:ring-[#7a8ba3]/40">
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="+234 000 000 0000"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#9aa3b3]"
                    />
                    <Pencil className="h-5 w-5 text-[#6b7381]" />
                  </div>
                </label>
              </div>

              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-start">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={personalSaveDisabled}
                  className={personalSaveClasses(
                    "w-full rounded-xl px-6 py-3 text-base font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#61013B] sm:w-auto"
                  )}
                >
                  {personalSaveLabel}
                </button>
              </div>
            </div>
          )}

          {activeTab === "password" && (
            <div className="flex max-w-3xl flex-col gap-8">
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-[#6b0c44]">Change Password</h2>
                <p className="text-sm text-gray-600">Create a new password you can remember easily</p>
              </div>

              <div className="space-y-5">
                <label className="block text-sm font-semibold text-gray-700">
                  Current Password
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#eddce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#b08ba3] focus-within:ring-1 focus-within:ring-[#b08ba3]/50">
                    <input
                      type={showPassword.currentPassword ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) => handlePasswordInput("currentPassword", e.target.value)}
                      placeholder="Enter current password"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#b3b3b3]"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("currentPassword")}
                      className="text-sm font-semibold text-[#7a0051] transition hover:text-[#5b003c]"
                    >
                      {showPassword.currentPassword ? "hide" : "show"}
                    </button>
                  </div>
                </label>

                <label className="block text-sm font-semibold text-gray-700">
                  New Password
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#eddce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#b08ba3] focus-within:ring-1 focus-within:ring-[#b08ba3]/50">
                    <input
                      type={showPassword.newPassword ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => handlePasswordInput("newPassword", e.target.value)}
                      placeholder="Enter new password"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#b3b3b3]"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("newPassword")}
                      className="text-sm font-semibold text-[#7a0051] transition hover:text-[#5b003c]"
                    >
                      {showPassword.newPassword ? "hide" : "show"}
                    </button>
                  </div>
                </label>

                <label className="block text-sm font-semibold text-gray-700">
                  Repeat Password
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#eddce6] bg-white px-4 py-3 shadow-sm focus-within:border-[#b08ba3] focus-within:ring-1 focus-within:ring-[#b08ba3]/50">
                    <input
                      type={showPassword.confirmPassword ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => handlePasswordInput("confirmPassword", e.target.value)}
                      placeholder="Repeat new password"
                      className="flex-1 bg-transparent text-base font-medium text-[#4a5261] outline-none placeholder:text-[#b3b3b3]"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("confirmPassword")}
                      className="text-sm font-semibold text-[#7a0051] transition hover:text-[#5b003c]"
                    >
                      {showPassword.confirmPassword ? "hide" : "show"}
                    </button>
                  </div>
                </label>

                {passwordMismatch && (
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#d72f3f]">
                    <AlertCircle className="h-4 w-4" />
                    <span>Password does not match</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-start gap-3">
                <button
                  type="button"
                  onClick={handlePasswordSave}
                  disabled={passwordSaving || !passwordFieldsComplete}
                  className={`rounded-xl px-6 py-3 text-base font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#61013B] ${
                    passwordSaving || !passwordFieldsComplete
                      ? "bg-[#c8a3b6]/70 cursor-not-allowed"
                      : "bg-[#61013B] hover:bg-[#4f0f32]"
                  }`}
                >
                  {passwordSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "privacy" && (
            <div className=" flex max-w-4xl flex-col gap-6">
              <div className="space-y-1">
                <h2 className="text-3xl font-bold text-[#6b0c44]">Privacy Policy</h2>
                <p className="text-md font-medium text-gray-600">
                  Effective Date: {privacyDate}
                </p>
              </div>
              <ol className="space-y-3 text-md font-medium leading-6 text-gray-700 list-decimal pl-5">
                {privacyItems.map((item) => (
                  <li key={item.title}>
                    <span className="font-semibold">{item.title}</span> {item.body}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {activeTab === "terms" && (
            <div className=" flex max-w-4xl flex-col gap-6">
              <div className="space-y-1">
                <h2 className="text-3xl font-bold text-[#6b0c44]">Terms & Condition</h2>
                <p className="text-md font-medium text-gray-600">
                  Effective Date: {termsDate}
                </p>
              </div>
              <ol className="space-y-3 text-md font-medium leading-6 text-gray-700 list-decimal pl-5">
                {termsItems.map((item) => (
                  <li key={item.title}>
                    <span className="font-semibold">{item.title}</span> {item.body}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {activeTab !== "personal" && activeTab !== "password" && activeTab !== "privacy" && activeTab !== "terms" && (
            <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed border-[#e6e8ef] bg-[#fafbfc] text-center text-sm text-gray-500">
              <p>Content for {navItems.find((item) => item.key === activeTab)?.label || "this section"} is coming soon.</p>
            </div>
          )}
        </section>
      </div>

      {passwordSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30  px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-8 text-center shadow-xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e8f7ed] text-green-600">
              <Check className="h-7 w-7" />
            </div>
            <div className="mt-6 space-y-2">
              <h3 className="text-2xl font-semibold text-[#6b0c44]">Password Reset</h3>
              <p className="text-sm font-medium text-[#4a5261]">You have successfully changed your password</p>
            </div>
            <button
              type="button"
              onClick={() => setPasswordSuccess(false)}
              className="mt-8 w-full rounded-xl border border-[#6b0c44] px-4 py-3 text-base font-semibold text-[#6b0c44] transition hover:bg-[#f7f1f5]"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
